import React, { useMemo } from 'react';
import { Box, List, Text } from '@common-fe/common-fe';

import { useGetEmployeeId } from '@/modules/employee/Employee/hooks';

import { useGetClaimServicesQuery } from '../../../../queries';
import { LMNClaim } from '../../LettersOfMedicalNecessity.types';
import AssociateWithClaimsListHeader from '../AssociateWithClaimsList/AssociateWithClaimsListHeader';
import AssociateWithClaimsListItem from '../AssociateWithClaimsList/AssociateWithClaimsListItem';

interface Props {
  testId?: string;
  title: string;
  claims?: LMNClaim[];
  isStillRequiringAttention?: boolean;
  isMobile?: boolean;
}

const ClaimsReviewList: React.FC<Props> = ({
  testId,
  title,
  claims,
  isStillRequiringAttention,
  isMobile,
}) => {
  const employeeId = useGetEmployeeId();
  const claimsServicesIds = useMemo(() => {
    const servicesIds = (claims?.map((claim) => claim?.serviceId)?.filter(Boolean) as string[]) || [];
    return [...new Set(servicesIds)];
  }, [claims]);

  const { services, isLoading: isServicesLoading } = useGetClaimServicesQuery({
    serviceIds: claimsServicesIds,
    enabled: !!claimsServicesIds?.length,
  });

  const claimsWithServices = useMemo(
    () =>
      claims?.map((claim) => {
        const claimService = services?.find((service) => service?.id === claim?.serviceId);
        return {
          ...claim,
          ...claimService,
        };
      }),
    [claims, services]
  );

  const children = useMemo(() => {
    if (isServicesLoading) {
      return <List.Preloader testId="associated-claims-list_preloader" />;
    }

    return claimsWithServices?.map((claim) => (
      <AssociateWithClaimsListItem
        key={claim?.id}
        employeeId={employeeId}
        claimId={claim?.claimId}
        providerName={claim?.providerName}
        categoryType={claim?.categoryType}
        serviceName={claim?.name}
        serviceOn={claim?.serviceOn}
        amount={claim?.requestedAmount}
        isMobile={isMobile}
        {...(isStillRequiringAttention
          ? {
              border: { color: 'warningBorder', size: '2px' },
            }
          : {
              background: 'infoContainer',
              border: { color: 'infoBorder', size: '2px' },
            })}
      />
    ));
  }, [isServicesLoading, claimsWithServices, employeeId, isMobile, isStillRequiringAttention]);

  return (
    <Box data-testid={testId}>
      <Text weight={700}>{title}</Text>
      <Box margin={{ top: 'spacing8' }}>
        <List>
          {isMobile ? null : <AssociateWithClaimsListHeader />}
          {children}
        </List>
      </Box>
    </Box>
  );
};

export default ClaimsReviewList;
